import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {getLanguage} from "../utils/sessionStorage.js";

// Define fallback translations for all keys
const defaultTranslations = {
  // Transaction-related messages
  'debit.confirm.title': 'Deposit',
  'credit.confirm.title': 'Withdrawal',

  'obv.message.ready': 'Your account is now ready.',
  'obv.labels.bank_account': 'Bank account:',
  'obv.labels.account': 'account',
  'obv.button.continue': 'Continue',
  'obv.button.confirm': 'Confirm',

  'debit.labels.from': 'FROM:',
  'debit.labels.amount': 'AMOUNT:',
  'debit.labels.to': 'TO:',

  'common.terms.agreement': "By confirming you agree to the <bold>Privacy Policy and Terms of Use</bold>",
  'common.transaction.completed': 'Transaction Completed',
  'common.transaction.cancelled': 'Transaction Cancelled',
  'common.transaction.result.safeToClose.description': 'This page should close automatically. If it does not close within 15 seconds, please close this page',
  'common.merchant': 'Merchant',

  // Loading messages
  'loading.message.processing': 'Processing',
  'loading.message.verifying': 'Verifying',

  // Generic error messages
  'error.generic.title': 'Sorry, our service couldn\'t process your request.',
  'error.generic.subtitle': 'No money was exchanged.',
  'error.generic.description': 'Please try again. If it doesn\'t work, come back at a later time.',
  'error.generic.back': 'Back to',

  // HTTP Status code errors
  // 400 Bad Request
  'error.400.title': 'Invalid request detected.',
  'error.400.subtitle': 'The request could not be completed.',
  'error.400.description': 'Your request contains invalid data. Please check your information and try again.',

  // 401 Unauthorized
  'error.401.title': 'Authorization required.',
  'error.401.subtitle': 'You need to sign in to access this service.',
  'error.401.description': 'Please log in or contact customer support if you believe this is a mistake.',

  // 403 Forbidden
  'error.403.title': 'Access denied.',
  'error.403.subtitle': 'You don\'t have permission to access this service.',
  'error.403.description': 'Please contact customer support if you believe you should have access.',

  // 404 Not Found
  'error.404.title': 'Resource not found.',
  'error.404.subtitle': 'The requested item doesn\'t exist.',
  'error.404.description': 'The page or resource you\'re looking for may have been moved or removed.',

  // 500 Internal Server Error
  'error.500.title': 'Server error occurred.',
  'error.500.subtitle': 'We\'re experiencing technical difficulties.',
  'error.500.description': 'Our team has been notified. Please try again later.',

  // Specific Error code messages
  // Error Code 37 & 41 - Identity verification
  'error.identity.title': 'Sorry, we could not verify your identity',
  'error.identity.subtitle': 'Please make sure that your details on #MERCHANT_NAME# match your bank account profile.',
  'error.identity.description': 'Continue your deposit by selecting another bank account.',

  // Error Code 68 - Transaction canceled
  'error.canceled.title': 'Transaction canceled.',
  'error.canceled.subtitle': 'No money was exchanged.',

  // Error Code 79 - Invalid account
  'error.invalid.account.title': 'Sorry, The selected account is invalid',
  'error.invalid.account.subtitle': 'No money was exchanged.',
  'error.invalid.account.description': 'Continue your deposit by selecting an account at a different bank.',

  // Error Code 72 - Unsupported account
  'error.unsupported.account.title': 'Sorry, this account is not supported',
  'error.unsupported.account.subtitle': 'Selected account: #MERCHANT_NAME# #ACCOUNT_MASK#. No money was exchanged.',
  'error.unsupported.account.description': 'Continue your deposit by selecting another bank account.',

  // Error Code 404_DEVICEDATA - Device data error
  'error.device.title': 'Sorry, our service couldn\'t process your request.',
  'error.device.subtitle': 'No money was exchanged [#DEVICE_ID#].',
  'error.device.description': 'Please try again. If it doesn\'t work, come back at a later time.'
};

const spanishTranslations = {
  // Transaction-related messages
  'debit.confirm.title': 'Depósito',
  'credit.confirm.title': 'Retiro',

  'obv.message.ready': 'Su cuenta ya está lista.',
  'obv.labels.bank_account': 'Cuenta bancaria:',
  'obv.labels.account': 'cuenta',
  'obv.button.continue': 'Continuar',
  'obv.button.confirm': 'Confirmar',

  'debit.labels.from': 'DE:',
  'debit.labels.amount': 'MONTO:',
  'debit.labels.to': 'A:',

  'common.terms.agreement': "Al confirmar, acepta la <bold>Política de Privacidad y los Términos de Uso</bold>",
  'common.transaction.completed': 'Transacción completada',
  'common.transaction.cancelled': 'Transacción cancelada',
  'common.transaction.result.safeToClose.description': 'Esta página debería cerrarse automáticamente. Si no se cierra en 15 segundos, por favor cierre esta página',
  'common.merchant': 'Comerciante',

  // Loading messages
  'loading.message.processing': 'Procesando',
  'loading.message.verifying': 'Verificando',

  // Generic error messages
  'error.generic.title': 'Lo sentimos, nuestro servicio no pudo procesar su solicitud.',
  'error.generic.subtitle': 'No se intercambió dinero.',
  'error.generic.description': 'Por favor, inténtelo de nuevo. Si no funciona, regrese más tarde.',
  'error.generic.back': 'Volver a',

  // HTTP Status code errors
  // 400 Bad Request
  'error.400.title': 'Solicitud inválida detectada.',
  'error.400.subtitle': 'No se pudo completar la solicitud.',
  'error.400.description': 'Su solicitud contiene datos inválidos. Por favor, verifique su información e inténtelo de nuevo.',

  // 401 Unauthorized
  'error.401.title': 'Se requiere autorización.',
  'error.401.subtitle': 'Necesita iniciar sesión para acceder a este servicio.',
  'error.401.description': 'Por favor, inicie sesión o contacte al soporte al cliente si cree que esto es un error.',

  // 403 Forbidden
  'error.403.title': 'Acceso denegado.',
  'error.403.subtitle': 'No tiene permiso para acceder a este servicio.',
  'error.403.description': 'Por favor, contacte al soporte al cliente si cree que debería tener acceso.',

  // 404 Not Found
  'error.404.title': 'Recurso no encontrado.',
  'error.404.subtitle': 'El elemento solicitado no existe.',
  'error.404.description': 'La página o el recurso que busca puede haber sido movido o eliminado.',

  // 500 Internal Server Error
  'error.500.title': 'Ocurrió un error en el servidor.',
  'error.500.subtitle': 'Estamos experimentando dificultades técnicas.',
  'error.500.description': 'Nuestro equipo ha sido notificado. Por favor, inténtelo más tarde.',

  // Specific Error code messages
  // Error Code 37 & 41 - Identity verification
  'error.identity.title': 'Lo sentimos, no pudimos verificar su identidad',
  'error.identity.subtitle': 'Por favor, asegúrese de que sus datos en #MERCHANT_NAME# coincidan con el perfil de su cuenta bancaria.',
  'error.identity.description': 'Continúe su depósito seleccionando otra cuenta bancaria.',

  // Error Code 68 - Transaction canceled
  'error.canceled.title': 'Transacción cancelada.',
  'error.canceled.subtitle': 'No se intercambió dinero.',

  // Error Code 79 - Invalid account
  'error.invalid.account.title': 'Lo sentimos, la cuenta seleccionada no es válida',
  'error.invalid.account.subtitle': 'No se intercambió dinero.',
  'error.invalid.account.description': 'Continúe su depósito seleccionando una cuenta en un banco diferente.',

  // Error Code 72 - Unsupported account
  'error.unsupported.account.title': 'Lo sentimos, esta cuenta no es compatible',
  'error.unsupported.account.subtitle': 'Cuenta seleccionada: #MERCHANT_NAME# #ACCOUNT_MASK#. No se intercambió dinero.',
  'error.unsupported.account.description': 'Continúe su depósito seleccionando otra cuenta bancaria.',

  // Error Code 404_DEVICEDATA - Device data error
  'error.device.title': 'Lo sentimos, nuestro servicio no pudo procesar su solicitud.',
  'error.device.subtitle': 'No se intercambió dinero [#DEVICE_ID#].',
  'error.device.description': 'Por favor, inténtelo de nuevo. Si no funciona, regrese más tarde.'
};

// Initialize i18n with fallback translations
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: defaultTranslations
      },
      es: {
        translation: spanishTranslations
      }
    },
    lng: 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    // Ensure missing keys return the specified default value
    returnNull: false,
    returnEmptyString: false,
    // Don't load any additional translations during init
    partialBundledLanguages: true,
    saveMissing: false
  });


/**
 * Function to detect and set language from URL parameter
 * Checks for 'lang' parameter in URL (e.g., ?lang=es)
 */
export const detectAndSetLanguageFromURL = () => {
  try {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const langParam = urlParams.get('language');

    if (langParam) {
      // Normalize language code to lowercase
      const normalizedLang = langParam.toLowerCase();

      // Check if the language is supported
      const supportedLanguages = ['en', 'es'];
      if (supportedLanguages.includes(normalizedLang)) {
        i18n.changeLanguage(normalizedLang);
        return normalizedLang;
      }
    }
  } catch (error) {
    console.error('Error detecting language from URL:', error);
  }

  // Return default language if no valid lang parameter found
  return 'en';
};

/**
 * Function to load custom translations from obvInfo
 * This replaces the ObvTranslationProvider
 */
export const loadTranslationsFromObvInfo = (obvInfo) => {

  const language = getLanguage() || 'en';
  // Get the current default translations
  const currentResources = i18n.getResourceBundle(language, 'translation') || {};
  // If custom texts exist, merge them with defaults
  if (obvInfo?.properties?.customTexts && Array.isArray(obvInfo.properties.customTexts)) {

    const customTexts = obvInfo.properties.customTexts;

    // Create translation object from customTexts
    const customTranslations = {};
    customTexts.forEach(item => {
      if (item.active && item.key && item.value && item.language === language) {
        customTranslations[item.key] = item.value;
      }
    });

    // Merge custom translations with defaults
    // New keys will be added, existing keys will be updated
    const mergedTranslations = {
      ...currentResources,
      ...customTranslations
    };

    // Add merged translations back to i18n
    i18n.addResourceBundle(language, 'translation', mergedTranslations, true, true);

    return true;
  } else {
    return false;
  }
};

export default i18n;